import { Theme } from '@parkingbase/ecommerce-api-test';
import { ThemeService } from '@/services/theme.service';

import { create } from 'zustand';
import { logger } from '@/lib/logger';

const themeService = new ThemeService();

const THEME_MODE_KEY = 'dark-mode';

export type ThemeMode = 'light' | 'dark' | 'system';

function persistThemeMode(mode: ThemeMode) {
  localStorage.setItem(THEME_MODE_KEY, mode);
  // Cookie stores the resolved boolean for SSR
  const resolved = mode === 'dark' ? 'true' : 'false';
  document.cookie = `${THEME_MODE_KEY}=${resolved}; path=/; max-age=31536000; SameSite=Lax`;
}

function resolveSystemPreference(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

interface ThemeState {
  theme: Theme | null;
  companyThemes: Record<string, Theme>;
  isLoading: boolean;
  themeMode: ThemeMode;
  isDarkMode: boolean;
  getDefaultTheme: () => Promise<void>;
  setTheme: (theme: Theme) => void;
  setThemeMode: (mode: ThemeMode) => void;
  setDarkMode: (value: boolean) => void;
  toggleDarkMode: () => void;
  getConvergeWebsiteThemes: () => Promise<void>;
}

function resolveIsDark(mode: ThemeMode): boolean {
  if (mode === 'system') return resolveSystemPreference();
  return mode === 'dark';
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: null,
  companyThemes: {},
  isLoading: false,
  themeMode: 'light',
  isDarkMode: false,
  setTheme: (theme: Theme) => set({ theme }),
  setThemeMode: (mode: ThemeMode) => {
    set({ themeMode: mode, isDarkMode: resolveIsDark(mode) });
    if (typeof window !== 'undefined') {
      persistThemeMode(mode);
    }
  },
  setDarkMode: (value: boolean) => {
    set({ isDarkMode: value, themeMode: value ? 'dark' : 'light' });
    if (typeof window !== 'undefined') {
      persistThemeMode(value ? 'dark' : 'light');
    }
  },
  toggleDarkMode: () => {
    const next = !get().isDarkMode;
    set({ isDarkMode: next, themeMode: next ? 'dark' : 'light' });
    if (typeof window !== 'undefined') {
      persistThemeMode(next ? 'dark' : 'light');
    }
  },
  getDefaultTheme: async () => {
    try {
      set({ isLoading: true });
      const defaultTheme = await themeService.getDefaultTheme();
      set({ theme: defaultTheme });
    } catch (error) {
      logger.error('Failed to fetch default theme', error, {
        feature: 'theme-store',
        route: 'getDefaultTheme',
      });
    } finally {
      set({ isLoading: false });
    }
  },
  getConvergeWebsiteThemes: async () => {
    try {
      const themes = await themeService.getConvergeWebsiteThemes();
      set({ companyThemes: themes });
    } catch (error) {
      logger.error('Failed to fetch converge website themes', error, {
        feature: 'theme-store',
        route: 'getConvergeWebsiteThemes',
      });
    }
  },
}));
